
import React, { forwardRef } from 'react'

import type { FieldProps } from './types'
import type { FieldValues, RegisterOptions } from 'react-hook-form'

import {FilledInput, FormControl, FormHelperText, Typography, type SxProps, useTheme} from '@mui/material'

import { Input } from '@repo/ui'




interface TextAreaFieldProps<T extends FieldValues = FieldValues>
  extends Omit<FieldProps<T>, 'startAdornment'> {
  name: string
  maxRows?: number
  minRows?: number
  helperText?: string
  label?: string
  rules?: RegisterOptions
  defaultValue?: string
  placeholder?: string
  type?: string
  sx?: SxProps
  onValueChange?: (value: string) => void
}

const TextAreaFieldImpl = forwardRef<HTMLInputElement, TextAreaFieldProps>(({
  register,
  name,
  helperText,
  formState: { errors },
  defaultValue = '',
  placeholder = '',
  minRows = 2,
  maxRows = 10,
  label,
  rules,
  sx,
  onValueChange,
}, ref) => {
  
  const registerProps = register(name, rules);
  
  return (
    <FormControl error={!!errors[name]} sx={{ width: '100%' }}>
      {label && (
        <Typography mb="8px" variant="body3">
          {label}
        </Typography>
      )}
      <Input
        ref={ref}
        // VRG-734 — without the register ref react-hook-form cannot write the node, so
        // `reset(record)` on an edit form left the textarea showing its default (blank) value.
        inputRef={registerProps.ref}
        type="text"
        sx={sx as object}
        fullWidth
        multiline={true}
        defaultValue={defaultValue}
        minRows={minRows}
        maxRows={maxRows}
        placeholder={placeholder}
        // The visible label above is a bare Typography, so name the control itself.
        inputProps={label ? { 'aria-label': label } : undefined}
        error={!!errors[name]}
        onValueChange={(value: string) => {
          // Convert the string value to an event-like object for react-hook-form
          const event = { target: { value, name } };
          registerProps.onChange(event);
          onValueChange?.(value);
        }}
        onBlur={registerProps.onBlur}
      />
      {errors[name] && <FormHelperText sx={{ margin: '4px 0 0' }}>{errors[name]?.message as string}</FormHelperText>}
      {helperText && <FormHelperText>{helperText}</FormHelperText>}
    </FormControl>
  )
})

/**
 * Re-exported under the generic signature so a caller's own form type flows through.
 * See the `FieldProps` docblock in `./types` for why the implementation above stays on the
 * default instantiation and why this assertion is the standard idiom rather than a
 * suppression. `forwardRef` erases type parameters, so the ref is restored by hand here.
 */
export const TextAreaField = TextAreaFieldImpl as <T extends FieldValues = FieldValues>(
  props: TextAreaFieldProps<T> & { ref?: React.Ref<HTMLInputElement> },
) => React.ReactElement | null
